/**
 * `analyseSections(originalHtml, options?)`: finds the editable slots of an
 * imported newsletter and proposes how to cut it into sections.
 *
 * Nothing here changes the HTML. The result only holds offsets into the
 * original string, which the renderer (`./render.ts`) uses to splice
 * slot values in and to reorder whole table rows. See
 * docs/section-editor-core.md for the model and the heuristics.
 */
import { findAttr, scan, type OpenToken, type ScannedAttr, type Token } from './scan.js';
import {
  collapseWhitespace,
  decodeEntities,
  hashString,
  hasVisibleText,
  inspectMergeTokens,
} from './text.js';
import {
  ADDRESS_HEURISTIC_PATTERN,
  footerSenderKind,
  MISSING_SENDER_MESSAGE,
  type SenderContext,
} from '../templates/sender.js';

export type SectionKind = 'movable' | 'locked-footer' | 'locked-custom';
export type SlotKind = 'text' | 'image' | 'link' | 'button';
export type Confidence = 'high' | 'medium' | 'low';
/** Why a slot's value cannot be changed. */
export type SlotProtection = 'unsubscribe' | 'sender';

/** Slots before the section rows (for example the preheader). */
export const CHUNK_TOP = -1;
/** Slots after the section rows. */
export const CHUNK_BOTTOM = -2;

export interface Span {
  start: number;
  end: number;
}

/** Where an attribute value sits in the source, and how to write a new one there. */
export interface AttrSite extends Span {
  mode: 'quoted' | 'unquoted' | 'bare' | 'missing';
  name: string;
}

interface SlotBase {
  /** Stable id built from the content around the slot; survives a re-parse of the same source. */
  id: string;
  /** Row index the slot lives in, or {@link CHUNK_TOP} / {@link CHUNK_BOTTOM}. */
  chunk: number;
  /** Offset in the source, for ordering. */
  at: number;
  /** Set when the value must not be changed (unsubscribe target, sender details). */
  protected: SlotProtection | null;
  /** Merge fields in the current value, as written. The UI shows them as chips; an edit must keep them well-formed. */
  mergeFields: string[];
  /** Inside an element with `display:none` (typically the preheader). */
  hidden: boolean;
}

export interface TextSlot extends SlotBase {
  kind: 'text';
  /** Plain text: entities decoded, whitespace collapsed, `\n` for `<br>`. */
  value: string;
  span: Span;
  /** How this run writes a line break (`<br>`, `<br />`, ...). */
  br: string;
  /** The same text inside an Outlook conditional comment in the same row. */
  twins: Span[];
}

export interface ImageTwin {
  src: string;
  srcSite: AttrSite;
  /** `null` for a VML fill, which has no description. */
  altSite: AttrSite | null;
}

export interface ImageSlot extends SlotBase {
  kind: 'image';
  src: string;
  /** `null` when the image has no alt attribute at all. */
  alt: string | null;
  srcSite: AttrSite;
  altSite: AttrSite;
  /** The Outlook duplicates of this image (inside conditional comments). */
  twins: ImageTwin[];
}

export interface LinkSlot extends SlotBase {
  kind: 'link' | 'button';
  href: string;
  hrefSite: AttrSite;
  /** The same address inside an Outlook conditional comment (for example a VML button). */
  twins: AttrSite[];
}

export type Slot = TextSlot | ImageSlot | LinkSlot;

export type ProblemCode =
  | 'image_missing_alt'
  | 'image_mailchimp_hosted'
  | 'image_embedded'
  | 'image_relative_src'
  | 'unknown_merge_tag'
  | 'outlook_fallback_differs'
  | 'missing_sender_details'
  | 'missing_unsubscribe_link';

export interface Problem {
  code: ProblemCode;
  message: string;
  /** The slot the problem sits on; absent for a problem of the whole mail. */
  slotId?: string;
}

export interface RowInfo {
  index: number;
  /** Source range of the row including the whitespace and comment above it. */
  start: number;
  end: number;
  /** A rule or spacer: no text, no link, no image taller than 30 px. */
  separator: boolean;
  /** Index of the block (content row plus the separators above it) this row belongs to. */
  block: number;
}

/**
 * A content row together with the separator rows directly above it. A
 * section is one or more whole blocks; a section can only start at a
 * block.
 */
export interface BlockInfo {
  /** Stable id of the content row. Also the id of a section that starts here. */
  id: string;
  /** First row (the first separator above, or the content row itself). */
  first: number;
  /** The content row. */
  content: number;
  /** Last row (the content row, or trailing separators for the last block). */
  last: number;
  name: string;
  /** A section may start here: the boundary above is a clean row boundary and it is not below the footer start. */
  canStart: boolean;
  /** Holds the unsubscribe link or the sender details. */
  marker: boolean;
  /** Tied to a neighbour (conditional comment between rows, rowspan): cannot be moved. */
  tied: boolean;
  /** Why the proposal starts a section here, or `null` when it does not. */
  proposed: { confidence: Confidence; reason: string } | null;
}

export interface SectionInfo {
  /** Id of the first block, or `frame-top` / `frame-bottom` / `frame-all`. */
  id: string;
  name: string;
  kind: SectionKind;
  confidence: Confidence;
  reason: string;
  /** Zero-based row range in the section table; `null` for content outside the rows. */
  rows: { first: number; last: number } | null;
  /** Block range; `null` for content outside the rows. */
  blocks: { first: number; last: number } | null;
  slotIds: string[];
}

export interface SectionDocument {
  /** Length and hash of the source this was computed from, so a caller can tell a stale document. */
  sourceLength: number;
  sourceHash: string;
  /** `rows`: one table's rows are the sections. `none`: no usable row structure, the mail is one locked section. */
  structure: 'rows' | 'none';
  structureReason: string;
  /** Everything before this offset and after `rowsEnd` is never reordered. */
  rowsStart: number;
  rowsEnd: number;
  rows: RowInfo[];
  blocks: BlockInfo[];
  /** Index of the block where the footer starts at the latest, or -1 when the rows hold no footer. */
  footerBlock: number;
  /** The proposed cut: ids of the blocks that start a section. */
  proposedCut: string[];
  /** Sections under the proposed cut, with the frame sections first/last. */
  sections: SectionInfo[];
  slots: Slot[];
  problems: Problem[];
}

// --- small helpers -------------------------------------------------------

const VOID_ELEMENTS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);
const ROW_GROUPS = new Set(['tbody', 'thead', 'tfoot']);

const MAILCHIMP_HOST_PATTERN =
  /^https?:\/\/(?:[^/?#]*\.)?(?:mcusercontent\.com|mailchimp\.com|list-manage\.com|chimpstatic\.com)(?:[/?#:]|$)/i;
const USABLE_IMG_SRC_PATTERN = /^(?:https?:\/\/|cid:|\{\{)/i;

function siteOf(attr: ScannedAttr): AttrSite {
  return {
    start: attr.valueStart,
    end: attr.valueEnd,
    mode: !attr.hasValue ? 'bare' : attr.quote === '' ? 'unquoted' : 'quoted',
    name: attr.name,
  };
}

function missingSite(token: OpenToken, name: string): AttrSite {
  const at = token.start + 1 + token.name.length;
  return { start: at, end: at, mode: 'missing', name };
}

function attrValue(html: string, token: OpenToken, name: string): string | undefined {
  const attr = findAttr(token, name);
  if (!attr || !attr.hasValue) return undefined;
  return decodeEntities(html.slice(attr.valueStart, attr.valueEnd));
}

function styleProp(style: string, name: string): string | undefined {
  const match = new RegExp(`(?:^|;)\\s*${name}\\s*:\\s*([^;]+)`, 'i').exec(style);
  return match ? match[1]!.trim().toLowerCase() : undefined;
}

function isConditionalComment(raw: string): boolean {
  return !raw.startsWith('<!--') || /\[if\s|\[endif\]/i.test(raw);
}

function isWhitespace(html: string, token: Token): boolean {
  return token.type === 'text' && !/[^ \t\r\n\f]/.test(html.slice(token.start, token.end));
}

const NO_COLOUR = new Set(['', '#fff', '#ffffff', 'white', 'transparent', 'none', 'inherit']);

/** The background colour an element sets itself, or '' when it sets none (white counts as none). */
function ownBackground(html: string, token: OpenToken): string {
  const style = attrValue(html, token, 'style') ?? '';
  const fromStyle = styleProp(style, 'background-color') ?? styleProp(style, 'background');
  const value = (fromStyle ?? attrValue(html, token, 'bgcolor') ?? '').trim().toLowerCase();
  return NO_COLOUR.has(value) ? '' : value;
}

function isZero(value: string | undefined): boolean {
  return value === undefined || /^0(?:px|pt|em|%)?$/.test(value);
}

/** True when the cell has no left/right padding of its own. */
function hasNoSidePadding(style: string): boolean {
  let left: string | undefined;
  let right: string | undefined;
  const shorthand = styleProp(style, 'padding');
  if (shorthand !== undefined) {
    const parts = shorthand
      .replace(/!important/g, '')
      .trim()
      .split(/\s+/);
    right = parts.length === 1 ? parts[0] : parts[1];
    left = parts.length === 4 ? parts[3] : right;
  }
  left = styleProp(style, 'padding-left') ?? left;
  right = styleProp(style, 'padding-right') ?? right;
  return isZero(left) && isZero(right);
}

function isFullWidth(html: string, token: OpenToken): boolean {
  const style = attrValue(html, token, 'style') ?? '';
  return (styleProp(style, 'width') ?? attrValue(html, token, 'width') ?? '').trim() === '100%';
}

function pixelHeight(html: string, token: OpenToken): number | null {
  const style = attrValue(html, token, 'style') ?? '';
  const raw = attrValue(html, token, 'height') ?? styleProp(style, 'height') ?? '';
  const match = /^(\d+)(?:px)?$/.exec(raw.trim());
  return match ? Number(match[1]) : null;
}

function isButton(html: string, token: OpenToken): boolean {
  const cls = attrValue(html, token, 'class') ?? '';
  if (/(?:^|[\s_-])(?:btn|button)(?:$|[\s_-])/i.test(cls)) return true;
  const style = attrValue(html, token, 'style') ?? '';
  const padding = styleProp(style, 'padding');
  if (padding === undefined || /^0(?:px)?(?:\s+0(?:px)?)*$/.test(padding)) return false;
  const display = styleProp(style, 'display') ?? '';
  const background = styleProp(style, 'background-color') ?? styleProp(style, 'background');
  return (
    display === 'block' ||
    display === 'inline-block' ||
    (background !== undefined && !NO_COLOUR.has(background))
  );
}

function shorten(text: string, max = 40): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max - 1).trimEnd()}…` : flat;
}

// --- pass 1: element tree, slots, conditional comments ------------------

interface RowNode {
  openIdx: number;
  /** Token index of the row's own `</tr>`, or -1 when it was closed by something else. */
  closeIdx: number;
  cells: OpenToken[];
  rowspan: boolean;
}

interface TableNode {
  openIdx: number;
  closeIdx: number;
  rows: RowNode[];
  /** A row or cell was closed without its own end tag: offsets of the rows cannot be trusted. */
  implied: boolean;
  hidden: boolean;
}

interface StackEl {
  name: string;
  token: OpenToken;
  hidden: boolean;
  table?: TableNode;
  row?: RowNode;
}

interface RawText {
  kind: 'text';
  at: number;
  value: string;
  span: Span;
  br: string;
  parentRaw: string;
  hidden: boolean;
}
interface RawImage {
  kind: 'image';
  at: number;
  token: OpenToken;
  src: string;
  alt: string | null;
  height: number | null;
  hidden: boolean;
}
interface RawLink {
  kind: 'link' | 'button';
  at: number;
  token: OpenToken;
  href: string;
  hidden: boolean;
}
type RawSlot = RawText | RawImage | RawLink;

interface TextRun {
  value: string;
  span: Span;
  br: string;
}

/** Turns a run of text and `<br>` tokens into one plain-text value, or `null` when it shows nothing. */
function readTextRun(html: string, run: Token[]): TextRun | null {
  let first = -1;
  let last = -1;
  run.forEach((token, i) => {
    if (
      token.type === 'text' &&
      hasVisibleText(decodeEntities(html.slice(token.start, token.end)))
    ) {
      if (first === -1) first = i;
      last = i;
    }
  });
  if (first === -1) return null;

  const firstToken = run[first]!;
  const lastToken = run[last]!;
  const lead = /^[ \t\r\n\f]*/.exec(html.slice(firstToken.start, firstToken.end))![0].length;
  const trail = /[ \t\r\n\f]*$/.exec(html.slice(lastToken.start, lastToken.end))![0].length;
  let br = '';
  let joined = '';
  for (let i = first; i <= last; i += 1) {
    const token = run[i]!;
    const raw = html.slice(token.start, token.end);
    if (token.type === 'text') {
      joined += decodeEntities(raw.replace(/[ \t\r\n\f]+/g, ' '));
    } else {
      if (br === '') br = raw;
      joined += '\n';
    }
  }
  const value = joined
    .split('\n')
    .map((line) => line.replace(/ {2,}/g, ' ').replace(/^ | $/g, ''))
    .join('\n');
  return {
    value,
    span: { start: firstToken.start + lead, end: lastToken.end - trail },
    br: br === '' ? '<br>' : br,
  };
}

interface ConditionalBlock extends Span {
  innerStart: number;
  innerEnd: number;
}

interface Pass1 {
  tokens: Token[];
  tables: TableNode[];
  slots: RawSlot[];
  conditionals: ConditionalBlock[];
  /** Offsets where a downlevel-revealed conditional (`<!--[if !mso]><!-->`) opens (+1) or closes (-1). */
  revealed: { at: number; delta: number }[];
}

const HIDDEN_CONDITIONAL = /^<!--\[if\s[^\]]*\]>(?!\s*<!--)/i;
const REVEALED_OPEN = /^(?:<!--\[if\s[^\]]*\]>\s*<!--+>|<!\[if\s[^\]]*\]>)$/i;
const REVEALED_CLOSE = /^(?:<!--\s*<!\[endif\]-->|<!\[endif\]>)$/i;
const HIDDEN_CLOSER = '<![endif]-->';

function pass1(html: string): Pass1 {
  const { tokens } = scan(html);
  const tables: TableNode[] = [];
  const slots: RawSlot[] = [];
  const conditionals: ConditionalBlock[] = [];
  const revealed: { at: number; delta: number }[] = [];
  const stack: StackEl[] = [];
  const hasBody = tokens.some((t) => t.type === 'open' && t.name === 'body');
  let inBody = !hasBody;
  let run: Token[] = [];

  const scopeTable = (): TableNode | undefined => {
    for (let j = stack.length - 1; j >= 0; j -= 1) {
      const table = stack[j]!.table;
      if (table) return table;
    }
    return undefined;
  };

  const flushRun = (): void => {
    if (run.length === 0) return;
    const read = inBody ? readTextRun(html, run) : null;
    run = [];
    if (!read) return;
    const parent = stack[stack.length - 1];
    slots.push({
      kind: 'text',
      at: read.span.start,
      value: read.value,
      span: read.span,
      br: read.br,
      parentRaw: parent ? html.slice(parent.token.start, parent.token.end) : '',
      hidden: parent?.hidden ?? false,
    });
  };

  /** Closes open elements called one of `names` inside the current table, as a browser does when a new row or cell starts. */
  const closeImplied = (names: string[], stopAt: string[]): void => {
    for (let j = stack.length - 1; j >= 0; j -= 1) {
      const el = stack[j]!;
      if (el.table || stopAt.includes(el.name)) return;
      if (names.includes(el.name)) {
        stack.length = j;
        const table = scopeTable();
        if (table) table.implied = true;
        return;
      }
    }
  };

  tokens.forEach((token, idx) => {
    if (token.type === 'text') {
      run.push(token);
      return;
    }
    if (token.type === 'open' && token.name === 'br') {
      run.push(token);
      return;
    }
    flushRun();

    if (token.type === 'comment') {
      const raw = html.slice(token.start, token.end);
      if (REVEALED_OPEN.test(raw)) revealed.push({ at: token.start, delta: 1 });
      else if (REVEALED_CLOSE.test(raw)) revealed.push({ at: token.start, delta: -1 });
      else if (inBody && HIDDEN_CONDITIONAL.test(raw) && raw.endsWith(HIDDEN_CLOSER)) {
        conditionals.push({
          start: token.start,
          end: token.end,
          innerStart: token.start + raw.indexOf(']>') + 2,
          innerEnd: token.end - HIDDEN_CLOSER.length,
        });
      }
      return;
    }
    if (token.type === 'raw') return;

    if (token.type === 'close') {
      const name = token.name;
      let j = stack.length - 1;
      for (; j >= 0; j -= 1) {
        const el = stack[j]!;
        if (el.name === name) break;
        if (el.table && name !== 'table') {
          j = -1;
          break;
        }
      }
      if (j < 0) return;
      const target = stack[j]!;
      for (let k = stack.length - 1; k > j; k -= 1) {
        if (stack[k]!.row) {
          const table = scopeTable();
          if (table) table.implied = true;
        }
      }
      stack.length = j;
      if (target.row) target.row.closeIdx = idx;
      if (target.table) target.table.closeIdx = idx;
      return;
    }

    // Opening tag.
    const name = token.name;
    if (name === 'body') inBody = true;
    if (name === 'tr') closeImplied(['tr'], []);
    else if (name === 'td' || name === 'th') closeImplied(['td', 'th'], ['tr']);
    else if (ROW_GROUPS.has(name)) closeImplied(['tr', ...ROW_GROUPS], []);

    const parent = stack[stack.length - 1];
    const style = attrValue(html, token, 'style') ?? '';
    const hidden = (parent?.hidden ?? false) || styleProp(style, 'display') === 'none';

    if (inBody) {
      if (name === 'img') {
        const src = attrValue(html, token, 'src');
        if (src !== undefined) {
          const alt = findAttr(token, 'alt');
          slots.push({
            kind: 'image',
            at: token.start,
            token,
            src: src.trim(),
            alt: alt ? collapseWhitespace(attrValue(html, token, 'alt') ?? '') : null,
            height: pixelHeight(html, token),
            hidden,
          });
        }
      } else if (name === 'a') {
        const href = attrValue(html, token, 'href');
        if (href !== undefined) {
          slots.push({
            kind: isButton(html, token) ? 'button' : 'link',
            at: token.start,
            token,
            href: href.trim(),
            hidden,
          });
        }
      }
    }

    if (VOID_ELEMENTS.has(name) || token.selfClosing) return;

    const el: StackEl = { name, token, hidden };
    if (name === 'table') {
      el.table = { openIdx: idx, closeIdx: -1, rows: [], implied: false, hidden };
      tables.push(el.table);
    } else if (name === 'tr') {
      const owner = parent?.table
        ? parent.table
        : parent && ROW_GROUPS.has(parent.name)
          ? stack[stack.length - 2]?.table
          : undefined;
      if (owner) {
        el.row = { openIdx: idx, closeIdx: -1, cells: [], rowspan: false };
        owner.rows.push(el.row);
      }
    } else if ((name === 'td' || name === 'th') && parent?.row) {
      parent.row.cells.push(token);
      if (Number.parseInt(attrValue(html, token, 'rowspan') ?? '1', 10) > 1) {
        parent.row.rowspan = true;
      }
    }
    stack.push(el);
  });
  flushRun();

  return { tokens, tables, slots, conditionals, revealed };
}

// --- pass 2: pick the section table --------------------------------------

interface Choice {
  table: TableNode | null;
  reason: string;
}

function chooseTable(p: Pass1): Choice {
  const total = p.slots.length;
  if (total === 0) return { table: null, reason: 'The mail has nothing editable.' };
  let best: TableNode | null = null;
  let bestScore = 0;
  let sawRows = false;
  let sawBroken = false;
  for (const table of p.tables) {
    if (table.rows.length < 2 || table.hidden) continue;
    sawRows = true;
    if (table.implied || table.closeIdx === -1 || table.rows.some((r) => r.closeIdx === -1)) {
      sawBroken = true;
      continue;
    }
    const from = p.tokens[table.rows[0]!.openIdx]!.start;
    const to = p.tokens[table.rows[table.rows.length - 1]!.closeIdx]!.end;
    let inside = 0;
    for (const slot of p.slots) if (slot.at >= from && slot.at < to) inside += 1;
    const coverage = inside / total;
    if (coverage < 0.5) continue;
    const score = table.rows.length * coverage * coverage;
    if (score > bestScore) {
      best = table;
      bestScore = score;
    }
  }
  if (best) return { table: best, reason: 'Sections are the rows of the main content table.' };
  if (sawBroken) {
    return {
      table: null,
      reason:
        'The content table has rows or cells without an end tag, so row boundaries cannot be trusted.',
    };
  }
  return {
    table: null,
    reason: sawRows
      ? 'No single table holds most of the content as rows.'
      : 'The content is not laid out as rows of one table.',
  };
}

// --- problems ------------------------------------------------------------

/** The values a slot shows right now (original, or with an edit applied). */
export interface SlotValues {
  text?: string;
  src?: string;
  alt?: string | null;
  href?: string;
  /** The Outlook duplicates will be written with the same image. */
  outlookSynced?: boolean;
}

/** Problems of one slot for the given values. Used for the original and for the edited view. */
export function slotProblems(slot: Slot, values: SlotValues, slotId: string = slot.id): Problem[] {
  const problems: Problem[] = [];
  const add = (code: ProblemCode, message: string): void => {
    problems.push({ code, message, slotId });
  };
  const checkTokens = (value: string): void => {
    const info = inspectMergeTokens(value);
    for (const unknown of info.unknown) add('unknown_merge_tag', unknown.message);
    if (info.malformed && info.unknown.length === 0) {
      add('unknown_merge_tag', 'A merge field is not written completely.');
    }
  };

  if (slot.kind === 'text') {
    checkTokens(values.text ?? slot.value);
  } else if (slot.kind === 'image') {
    const src = values.src ?? slot.src;
    const alt = values.alt === undefined ? slot.alt : values.alt;
    if (alt === null) add('image_missing_alt', 'The image has no description (alt text).');
    else checkTokens(alt);
    if (/^data:/i.test(src)) {
      add('image_embedded', 'The image is embedded in the HTML instead of being hosted.');
    } else if (MAILCHIMP_HOST_PATTERN.test(src)) {
      add('image_mailchimp_hosted', 'The image is still hosted at Mailchimp.');
    } else if (!USABLE_IMG_SRC_PATTERN.test(src)) {
      add('image_relative_src', 'The image address has no domain, so it would show as broken.');
    }
    if (values.outlookSynced !== true && slot.twins.some((twin) => twin.src !== slot.src)) {
      add('outlook_fallback_differs', 'Outlook shows a different image file here.');
    }
  } else {
    checkTokens(values.href ?? slot.href);
  }
  return problems;
}

// --- main ----------------------------------------------------------------

function withOrdinals(keys: string[]): string[] {
  const counts = new Map<string, number>();
  for (const key of keys) counts.set(key, (counts.get(key) ?? 0) + 1);
  const seen = new Map<string, number>();
  return keys.map((key) => {
    const n = counts.get(key)!;
    if (n === 1) return key;
    const i = (seen.get(key) ?? 0) + 1;
    seen.set(key, i);
    return `${key}.${i}of${n}`;
  });
}

/** Sections for a cut (block indices that start a section, ascending, starting with 0). */
export function sectionsForCut(doc: SectionDocument, starts: number[]): SectionInfo[] {
  const sections: SectionInfo[] = [];
  const lastMarker = doc.blocks.reduce((acc, block, i) => (block.marker ? i : acc), -1);
  starts.forEach((first, i) => {
    const last = (starts[i + 1] ?? doc.blocks.length) - 1;
    const head = doc.blocks[first]!;
    const blocks = doc.blocks.slice(first, last + 1);
    const rows = { first: head.first, last: blocks[blocks.length - 1]!.last };
    let kind: SectionKind = 'movable';
    let confidence: Confidence = head.proposed?.confidence ?? 'high';
    let reason = head.proposed?.reason ?? 'Set in the editor.';
    let name = head.name;
    if (lastMarker !== -1 && last >= lastMarker) {
      kind = 'locked-footer';
      confidence = 'high';
      reason = 'Holds the unsubscribe link or the sender details, so it stays last.';
      name = 'Footer';
    } else if (blocks.some((b) => b.marker)) {
      kind = 'locked-custom';
      confidence = 'low';
      reason =
        'Holds an unsubscribe link or sender details above the footer, so it stays in place.';
    } else if (blocks.some((b) => b.tied)) {
      kind = 'locked-custom';
      confidence = 'low';
      reason =
        'Rows here are tied together (Outlook conditional comment or rowspan), so it stays in place.';
    }
    sections.push({
      id: head.id,
      name,
      kind,
      confidence,
      reason,
      rows,
      blocks: { first, last },
      slotIds: doc.slots
        .filter((s) => s.chunk >= rows.first && s.chunk <= rows.last)
        .map((s) => s.id),
    });
  });
  return sections;
}

/** The locked sections for content before and after the rows (or the whole mail when there are no rows). */
export function frameSections(doc: SectionDocument): {
  top: SectionInfo | null;
  bottom: SectionInfo | null;
} {
  const make = (chunk: number): SectionInfo | null => {
    const slots = doc.slots.filter((s) => s.chunk === chunk);
    if (slots.length === 0 && doc.structure === 'rows') return null;
    const all = doc.structure === 'none';
    const firstText = slots.find((s): s is TextSlot => s.kind === 'text');
    const holdsFooter =
      !all && chunk === CHUNK_BOTTOM && doc.footerBlock === -1 && slots.some((s) => s.protected);
    return {
      id: all ? 'frame-all' : chunk === CHUNK_TOP ? 'frame-top' : 'frame-bottom',
      name: all
        ? 'Whole mail'
        : holdsFooter
          ? 'Footer'
          : firstText?.hidden
            ? 'Preheader'
            : chunk === CHUNK_TOP
              ? 'Above the sections'
              : 'Below the sections',
      kind: holdsFooter ? 'locked-footer' : 'locked-custom',
      confidence: all ? 'low' : 'high',
      reason: all
        ? doc.structureReason
        : holdsFooter
          ? 'Holds the unsubscribe link or the sender details, so it stays last.'
          : 'Outside the section rows, so it stays in place.',
      rows: null,
      blocks: null,
      slotIds: slots.map((s) => s.id),
    };
  };
  if (doc.structure === 'none') return { top: make(CHUNK_TOP), bottom: null };
  return { top: make(CHUNK_TOP), bottom: make(CHUNK_BOTTOM) };
}

export interface AnalyseOptions {
  /** Brand kit sender details for the footer check; without them it uses text patterns only. */
  sender?: SenderContext | undefined;
}

export function analyseSections(
  originalHtml: string,
  options: AnalyseOptions = {},
): SectionDocument {
  const html = originalHtml;
  const p = pass1(html);
  const { tokens } = p;
  const choice = chooseTable(p);
  const table = choice.table;

  // Row units: each row owns the whitespace and comments between the
  // previous row's end and its own end.
  const rows: RowInfo[] = [];
  const rowNames: (string | null)[] = [];
  const fused: boolean[] = [];
  let rowsStart = html.length;
  let rowsEnd = html.length;

  if (table) {
    const depthAt = (offset: number): number =>
      p.revealed.reduce((sum, e) => (e.at < offset ? sum + e.delta : sum), 0);
    const nodes = table.rows;
    let lead = nodes[0]!.openIdx - 1;
    let firstName: string | null = null;
    for (; lead >= 0; lead -= 1) {
      const token = tokens[lead]!;
      if (isWhitespace(html, token)) continue;
      const raw = html.slice(token.start, token.end);
      if (token.type === 'comment' && !isConditionalComment(raw)) {
        firstName ??= shorten(raw.slice(4, -3));
        continue;
      }
      break;
    }
    rowsStart = lead >= 0 ? tokens[lead]!.end : 0;
    rowsEnd = tokens[nodes[nodes.length - 1]!.closeIdx]!.end;

    nodes.forEach((node, i) => {
      const end = tokens[node.closeIdx]!.end;
      let start = rowsStart;
      let name = firstName;
      let tied = false;
      if (i > 0) {
        const prev = nodes[i - 1]!;
        start = tokens[prev.closeIdx]!.end;
        name = null;
        for (let k = prev.closeIdx + 1; k < node.openIdx; k += 1) {
          const token = tokens[k]!;
          if (isWhitespace(html, token)) continue;
          const raw = html.slice(token.start, token.end);
          if (token.type === 'comment' && !isConditionalComment(raw)) {
            name = shorten(raw.slice(4, -3));
          } else {
            tied = true;
          }
        }
        if (depthAt(start) !== 0 || prev.rowspan) tied = true;
      }
      rows.push({ index: i, start, end, separator: false, block: -1 });
      rowNames.push(name === '' ? null : name);
      fused.push(tied);
    });
  }

  const chunkOf = (offset: number): number => {
    if (offset < rowsStart) return CHUNK_TOP;
    if (offset >= rowsEnd) return CHUNK_BOTTOM;
    let lo = 0;
    let hi = rows.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (offset < rows[mid]!.end) hi = mid;
      else lo = mid + 1;
    }
    return lo;
  };

  // Row hashes: the naming comment plus the tag skeleton of the row. Line
  // breaks are part of the text, not of the skeleton, so rewording a
  // headline at the source does not give its row a new id.
  const rowHash: string[] = [];
  if (table) {
    table.rows.forEach((node, i) => {
      let skeleton = rowNames[i] ?? '';
      for (let k = node.openIdx; k <= node.closeIdx; k += 1) {
        const token = tokens[k]!;
        if (token.type === 'open' && token.name === 'br') continue;
        if (token.type === 'open') skeleton += `<${token.name}`;
        else if (token.type === 'close') skeleton += `>${token.name}`;
      }
      rowHash.push(hashString(skeleton));
    });
  }
  const chunkKey = (chunk: number): string =>
    chunk === CHUNK_TOP ? 'top' : chunk === CHUNK_BOTTOM ? 'bottom' : rowHash[chunk]!;

  // Slots with ids.
  const rawKeys = p.slots.map((raw) => {
    const where = chunkKey(chunkOf(raw.at));
    if (raw.kind === 'text') return `t${hashString(`${where}|${raw.parentRaw}|${raw.value}`)}`;
    const tagRaw = html.slice(raw.token.start, raw.token.end);
    return `${raw.kind[0]!}${hashString(`${where}|${tagRaw}`)}`;
  });
  const ids = withOrdinals(rawKeys);
  const slots: Slot[] = p.slots.map((raw, i): Slot => {
    const base = {
      id: ids[i]!,
      chunk: chunkOf(raw.at),
      at: raw.at,
      protected: null,
      hidden: raw.hidden,
    };
    if (raw.kind === 'text') {
      const info = inspectMergeTokens(raw.value);
      return {
        ...base,
        kind: 'text',
        protected: info.unsubscribe ? 'unsubscribe' : info.sender ? 'sender' : null,
        mergeFields: info.tokens,
        value: raw.value,
        span: raw.span,
        br: raw.br,
        twins: [],
      };
    }
    if (raw.kind === 'image') {
      const altAttr = findAttr(raw.token, 'alt');
      return {
        ...base,
        kind: 'image',
        mergeFields: inspectMergeTokens(raw.alt ?? '').tokens,
        src: raw.src,
        alt: raw.alt,
        srcSite: siteOf(findAttr(raw.token, 'src')!),
        altSite: altAttr ? siteOf(altAttr) : missingSite(raw.token, 'alt'),
        twins: [],
      };
    }
    const info = inspectMergeTokens(raw.href);
    return {
      ...base,
      kind: raw.kind,
      protected: info.unsubscribe ? 'unsubscribe' : null,
      mergeFields: info.tokens,
      href: raw.href,
      hrefSite: siteOf(findAttr(raw.token, 'href')!),
      twins: [],
    };
  });

  // A written postal address (templates from before 6/10-2026) is
  // protected only when no merge field provides the sender details: the
  // last text that looks like an address. A written sender line such as
  // "Copyright (C) 2026 NoPain® Cream" stays editable (the year changes);
  // the save-time server check still refuses a footer that loses it.
  if (!slots.some((s) => s.protected === 'sender')) {
    for (let i = slots.length - 1; i >= 0; i -= 1) {
      const slot = slots[i]!;
      if (slot.kind === 'text' && !slot.protected && ADDRESS_HEURISTIC_PATTERN.test(slot.value)) {
        slot.protected = 'sender';
        break;
      }
    }
  }

  // Outlook duplicates inside conditional comments, paired within the same row.
  const problems: Problem[] = [];
  for (const block of p.conditionals) {
    const chunk = chunkOf(block.start);
    const peers = slots.filter((s) => s.chunk === chunk);
    const inner = scan(html, block.innerStart, block.innerEnd).tokens;
    const images = peers.filter((s): s is ImageSlot => s.kind === 'image');
    const innerImages = inner.filter((t) => t.type === 'open' && t.name === 'img');
    let run: Token[] = [];
    const flush = (): void => {
      const read = run.length > 0 ? readTextRun(html, run) : null;
      run = [];
      if (!read) return;
      const peer = peers.find(
        (s): s is TextSlot =>
          s.kind === 'text' &&
          s.value === read.value &&
          !s.twins.some((t) => t.start === read.span.start),
      );
      if (peer) peer.twins.push(read.span);
    };
    for (const token of inner) {
      if (token.type === 'text' || (token.type === 'open' && token.name === 'br')) {
        run.push(token);
        continue;
      }
      flush();
      if (token.type !== 'open') continue;
      const srcAttr = findAttr(token, 'src');
      if (srcAttr?.hasValue) {
        const src = decodeEntities(html.slice(srcAttr.valueStart, srcAttr.valueEnd)).trim();
        const altAttr = findAttr(token, 'alt');
        const alt = altAttr ? collapseWhitespace(attrValue(html, token, 'alt') ?? '') : '';
        let peer = images.find((s) => s.src === src);
        if (!peer && token.name === 'img') {
          peer =
            (alt !== '' ? images.find((s) => s.alt === alt) : undefined) ??
            (images.length === 1 && innerImages.length === 1 ? images[0] : undefined);
        }
        if (peer) {
          peer.twins.push({
            src,
            srcSite: siteOf(srcAttr),
            altSite:
              token.name === 'img' ? (altAttr ? siteOf(altAttr) : missingSite(token, 'alt')) : null,
          });
        }
      }
      const hrefAttr = findAttr(token, 'href');
      if (hrefAttr?.hasValue) {
        const href = decodeEntities(html.slice(hrefAttr.valueStart, hrefAttr.valueEnd)).trim();
        const peer = peers.find(
          (s): s is LinkSlot => (s.kind === 'link' || s.kind === 'button') && s.href === href,
        );
        if (peer) peer.twins.push(siteOf(hrefAttr));
      }
    }
    flush();
  }

  // Separators, blocks, markers.
  const blocks: BlockInfo[] = [];
  let footerBlock = -1;
  const proposedCut: string[] = [];
  let structure: 'rows' | 'none' = table ? 'rows' : 'none';
  let structureReason = choice.reason;

  if (table) {
    const byRow: Slot[][] = rows.map(() => []);
    for (const slot of slots) if (slot.chunk >= 0) byRow[slot.chunk]!.push(slot);
    const heights = new Map<number, number | null>();
    for (const raw of p.slots) if (raw.kind === 'image') heights.set(raw.at, raw.height);

    rows.forEach((row, i) => {
      const tiedToNeighbour = fused[i]! || (fused[i + 1] ?? false);
      row.separator =
        !tiedToNeighbour &&
        byRow[i]!.every((slot) => {
          if (slot.kind !== 'image') return false;
          const height = heights.get(slot.at) ?? null;
          return height !== null && height <= 30;
        });
    });

    const contentRows = rows.filter((r) => !r.separator).map((r) => r.index);
    if (contentRows.length === 0) {
      structure = 'none';
      structureReason = 'The content table only holds spacer rows.';
    } else {
      // What the row looks like as a band: its own background colour.
      const band = (rowIndex: number): string => {
        const node = table.rows[rowIndex]!;
        for (const cell of node.cells) {
          const own = ownBackground(html, cell);
          if (own !== '') return own;
        }
        const cell = node.cells[0];
        if (node.cells.length !== 1 || !cell) return '';
        if (!hasNoSidePadding(attrValue(html, cell, 'style') ?? '')) return '';
        for (let k = node.openIdx + 1; k < node.closeIdx; k += 1) {
          const token = tokens[k]!;
          if (token.type === 'open' && token.name === 'table') {
            return isFullWidth(html, token) ? ownBackground(html, token) : '';
          }
        }
        return '';
      };

      const blockKeys = contentRows.map((r) => `r${rowHash[r]!}`);
      const blockIds = withOrdinals(blockKeys);
      const anySeparator = rows.some((r) => r.separator);
      contentRows.forEach((content, k) => {
        const first = k === 0 ? 0 : contentRows[k - 1]! + 1;
        const last = k === contentRows.length - 1 ? rows.length - 1 : content;
        for (let r = first; r <= last; r += 1) rows[r]!.block = k;
        const rowSlots = byRow[content]!;
        const firstText = rowSlots.find((s): s is TextSlot => s.kind === 'text' && !s.hidden);
        const firstImage = rowSlots.find((s): s is ImageSlot => s.kind === 'image' && !!s.alt);
        const hasLead = content > first;
        let proposed: BlockInfo['proposed'] = null;
        if (k === 0) proposed = { confidence: 'high', reason: 'First row of the mail.' };
        else if (!anySeparator) {
          proposed = rowNames[content]
            ? {
                confidence: 'medium',
                reason: 'The mail has no divider rows; every row is a section.',
              }
            : {
                confidence: 'low',
                reason: 'The mail has no divider rows or comments; every row is a section.',
              };
        } else if (hasLead) {
          proposed = { confidence: 'high', reason: 'Starts at a divider row.' };
        } else {
          const own = band(content);
          if (own !== '' && own !== band(contentRows[k - 1]!)) {
            proposed = {
              confidence: 'medium',
              reason: 'Starts where the background colour changes.',
            };
          }
        }
        blocks.push({
          id: blockIds[k]!,
          first,
          content,
          last,
          name:
            rowNames[content] ??
            (firstText
              ? shorten(firstText.value)
              : firstImage
                ? shorten(firstImage.alt!)
                : `Row ${content + 1}`),
          canStart: !fused[first]!,
          marker: rowSlots.some((s) => s.protected !== null),
          tied: fused[content]! || (fused[content + 1] ?? false),
          proposed,
        });
      });

      // Footer: the trailing run of marker blocks, widened to the proposed section around its first block.
      const lastMarker = blocks.reduce((acc, b, i) => (b.marker ? i : acc), -1);
      if (lastMarker !== -1) {
        footerBlock = lastMarker;
        while (footerBlock > 0 && blocks[footerBlock - 1]!.marker) footerBlock -= 1;
      }
      blocks.forEach((block, k) => {
        if (k === 0) return;
        if (!block.canStart) block.proposed = null;
        if (footerBlock !== -1 && k > footerBlock) {
          block.proposed = null;
          block.canStart = false;
        }
      });
      for (const block of blocks) if (block.proposed) proposedCut.push(block.id);
    }
  }

  if (structure === 'none') {
    rows.length = 0;
    rowsStart = html.length;
    rowsEnd = html.length;
    for (const slot of slots) slot.chunk = CHUNK_TOP;
  }

  for (const slot of slots) {
    problems.push(
      ...slotProblems(
        slot,
        slot.kind === 'text'
          ? { text: slot.value }
          : slot.kind === 'image'
            ? { src: slot.src, alt: slot.alt }
            : { href: slot.href },
      ),
    );
  }
  if (!slots.some((s) => s.protected === 'unsubscribe')) {
    problems.push({
      code: 'missing_unsubscribe_link',
      message: 'The mail has no unsubscribe link.',
    });
  }
  if (
    !slots.some((s) => s.protected === 'sender') &&
    footerSenderKind(html, options.sender) === 'missing'
  ) {
    problems.push({ code: 'missing_sender_details', message: MISSING_SENDER_MESSAGE });
  }

  const doc: SectionDocument = {
    sourceLength: html.length,
    sourceHash: hashString(html),
    structure,
    structureReason,
    rowsStart,
    rowsEnd,
    rows,
    blocks,
    footerBlock,
    proposedCut,
    sections: [],
    slots,
    problems,
  };
  const frames = frameSections(doc);
  const starts = blocks.map((b, i) => (b.proposed ? i : -1)).filter((i) => i >= 0);
  doc.sections = [
    ...(frames.top ? [frames.top] : []),
    ...sectionsForCut(doc, starts),
    ...(frames.bottom ? [frames.bottom] : []),
  ];
  return doc;
}
